import React from 'react';
import { useOAuthCallback } from '../hooks/useOAuthCallback';
import { authApi } from '../api/auth';
import WelcomeModal from '../components/WelcomeModal';

/**
 * Apple OAuth Callback Handler
 *
 * ARCHITECTURE NOTE:
 * Apple OAuth uses a redirect-based authorization code flow (replacing Apple Sign In SDK).
 * This component handles the redirect callback from Apple after user authentication.
 *
 * FLOW:
 * 1. User clicks "Sign in with Apple" → SocialLogin initiates OAuth flow
 * 2. User authenticates on Apple → Apple redirects back with authorization code
 * 3. This component receives code & state → Exchanges code for tokens via backend
 * 4. Backend validates CSRF state, generates JWT client_secret with ES256 signing, and exchanges code
 * 5. Backend returns complete AuthResponse (access_token, refresh_token, user)
 * 6. Component calls handleOAuthCallback() to store tokens and update AuthContext
 * 7. Navigate to dashboard → User is authenticated
 *
 * APPLE-SPECIFIC NOTES:
 * - Apple requires JWT signing with ES256 algorithm using a private key (.p8 file)
 * - The client_secret is dynamically generated on each token exchange (not a static secret)
 * - Backend handles JWT generation using APPLE_TEAM_ID, APPLE_KEY_ID, and APPLE_PRIVATE_KEY
 * - Redirect URI must exactly match the one configured in Apple Developer Portal
 *
 * See also: SocialLogin.tsx (initiation), AuthContext.tsx (handleOAuthCallback), saturday-night.md (Apple setup guide), useOAuthCallback.ts (shared logic)
 */
const AppleCallback: React.FC = () => {
  const { showWelcome, userInfo, handleWelcomeClose } = useOAuthCallback(
    'apple',
    ({ code, state }) => {
      const redirectUri = `${window.location.origin}/auth/apple/callback`;
      return authApi.appleCallback({ code, redirect_uri: redirectUri, state });
    }
  );

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900">Completing Apple Sign In...</h2>
          <p className="text-gray-600 mt-2">Please wait while we authenticate your account.</p>
        </div>
      </div>
      {showWelcome && userInfo && (
        <WelcomeModal
          isOpen={showWelcome}
          displayName={userInfo.displayName}
          email={userInfo.email}
          provider="apple"
          onClose={handleWelcomeClose}
        />
      )}
    </>
  );
};

export default AppleCallback;
